import { useEffect, useState, useCallback, Fragment } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import { useNavigate } from "react-router-dom";

const ReportTemperatureHistoryDateList = ({ daysToCompare, dateFrom, dateTo, onSelectedDatesChange }) => {
    const navigate = useNavigate();
    const [calender, setCalender] = useState([]);
    const [calendarItemBackground, setCalendarItemBackground] = useState([]);
    const [value, setValue] = useState("ALL");
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || 'dark');
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Get theme-aware selected background color
    const getSelectedBackground = useCallback(() => {
        return getThemeColor('#cccccc', 'rgb(77, 77, 77)');
    }, []);

    // Listen for theme changes
    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    // Also listen for the global theme change event
    useEffect(() => {
        if (global.gw.themeChanged) {
            setCurrentTheme(global.gw.theme || 'dark');
            setForceUpdate(prev => prev + 1);
        }
    }, [global.gw.themeChanged]);

    // Poll for theme changes (fallback)
    useEffect(() => {
        const interval = setInterval(() => {
            const currentThemeValue = global.gw.theme || 'dark';
            if (currentThemeValue !== currentTheme) {
                setCurrentTheme(currentThemeValue);
                setForceUpdate(prev => prev + 1);
                // Update calendar item backgrounds when theme changes
                const selectedBg = getSelectedBackground();
                let calendarTimes = window.document.getElementsByClassName("calendarTime");
                let bkgs = [];
                Array.from(calendarTimes).map((c, i) => {
                    const idx = c.getAttribute?.('data-calendar-index') ?? c.attributes?.calendarIndex?.value;
                    if (idx !== null && idx !== undefined) {
                        const currentBg = c.style.background || c.style.backgroundColor;
                        // If it was selected (had a background), update to new theme's selected color
                        if (currentBg && currentBg !== '' && currentBg !== 'transparent') {
                            bkgs[idx] = selectedBg;
                        } else {
                            bkgs[idx] = '';
                        }
                    }
                });
                if (bkgs.length > 0) {
                    setCalendarItemBackground(bkgs);
                }
            }
        }, 100);
        return () => clearInterval(interval);
    }, [currentTheme, getSelectedBackground]);

    useEffect(() => {
        global.gw.selectedSite.temperatureComparison.daysToCompare = daysToCompare;
    }, [daysToCompare]);

    useEffect(() => {
        (async () => {
            if (dateFrom && dateTo) {
                global.gw.selectedSite.temperatureComparison.historyDayDates = await fetchHistoryDatesByDateRange(dateFrom, dateTo);
            }
        })();
    }, [dateFrom, dateTo]);

    const handleCalendarSelectionChange = (e) => {
        const clicked = e.currentTarget;
        const clickedIdxAttr = clicked?.getAttribute?.('data-calendar-index');
        if (clickedIdxAttr == null || clickedIdxAttr === '') return;

        const clickedIdx = parseInt(clickedIdxAttr, 10);
        if (Number.isNaN(clickedIdx)) return;

        const selectedBg = getSelectedBackground();
        const lightSelectedBg = '#cccccc';
        const darkSelectedBg = 'rgb(77, 77, 77)';
        const isBgSelected = (bg) => {
            if (bg == null || bg === '' || bg === 'transparent') return false;
            return (
                bg === selectedBg
                || bg === lightSelectedBg
                || bg === darkSelectedBg
                || bg === 'rgb(77,77,77)'
            );
        };

        // Use React state (sx/background is not on element.style — reading DOM cleared other selections).
        const prev = Array.isArray(calendarItemBackground) ? calendarItemBackground : [];
        const calendarTimes = window.document.getElementsByClassName("calendarTime");
        const indices = new Set();
        Array.from(calendarTimes).forEach((c) => {
            const idx = c.getAttribute?.('data-calendar-index');
            if (idx != null && idx !== '') indices.add(parseInt(idx, 10));
        });
        indices.add(clickedIdx);

        const bkgs = [];
        indices.forEach((i) => {
            if (Number.isNaN(i)) return;
            const selected = isBgSelected(prev[i]);
            if (i === clickedIdx) {
                bkgs[i] = selected ? '' : selectedBg;
            } else {
                bkgs[i] = selected ? selectedBg : '';
            }
        });
        setCalendarItemBackground(bkgs);

        const selectedDates = Array.from(calendarTimes)
            .filter((c) => {
                const idx = c.getAttribute?.('data-calendar-index');
                if (idx == null) return false;
                return isBgSelected(bkgs[parseInt(idx, 10)]);
            })
            .map((c) => c.getAttribute?.('data-date'))
            .filter(Boolean)
            .map((d) => new Date((d || '').split('T')[0]));
        const days = selectedDates.length > 0
            ? Math.ceil((Math.max(...selectedDates) - Math.min(...selectedDates)) / (24 * 60 * 60 * 1000)) + 1
            : 0;
        onSelectedDatesChange?.(days);
    };

    const handleRadioButtonChange = (e, s) => {
        setValue(s);

        global.gw.selectedSite.temperatureComparison.isAm = null;
        let bkgs = [];
        let calendarTimes = window.document.getElementsByClassName("calendarTime");
        const selectedBg = getSelectedBackground();
        Array.from(calendarTimes).map((c, i) => {
            const idx = c.getAttribute?.('data-calendar-index') ?? c.attributes?.calendarIndex?.value;
            if (idx === null || idx === undefined) return;
            if (s === "NONE") {
                bkgs[idx] = "";
            }
            else if (s === "AM") {
                if (c.innerText.indexOf("AM") > 0) {
                    bkgs[idx] = selectedBg;
                }
                else {
                    bkgs[idx] = "";
                }
            }
            else if (s === "PM") {
                if (c.innerText.indexOf("PM") > 0) {
                    bkgs[idx] = selectedBg;
                }
                else {
                    bkgs[idx] = "";
                }
            }
            else {
                bkgs[idx] = selectedBg;
            }
        });

        setCalendarItemBackground(bkgs);

        const selectedDates = Array.from(calendarTimes)
            .filter(c => {
                const idx = c.getAttribute?.('data-calendar-index');
                return idx != null && bkgs[parseInt(idx, 10)] === selectedBg;
            })
            .map(c => c.getAttribute?.('data-date'))
            .filter(Boolean)
            .map(d => new Date((d || '').split('T')[0]));
        const days = selectedDates.length > 0
            ? Math.ceil((Math.max(...selectedDates) - Math.min(...selectedDates)) / (24 * 60 * 60 * 1000)) + 1
            : 0;
        onSelectedDatesChange?.(days);
    };

    const fetchHistoryDatesByDateRange = async (fromDate, toDate) => {
        const from = fromDate instanceof Date ? fromDate : new Date(fromDate);
        const to = toDate instanceof Date ? toDate : new Date(toDate);
        var response = await createApiEndpoint().fetchUserSiteAllHistoryDatesWithDateRange("compressed", from, to);

        var data = [];
        if (!response) {
            navigate("/logout");
            return;
        }
        if (response.data && response.data.fetchUserSiteAllHistoryDatesWithDateRange) {
            data = response.data.fetchUserSiteAllHistoryDatesWithDateRange;
        }
        else {
            // Fallback: backend may not have the new query yet - use days-based API
            const daysDiff = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
            return fetchHistoryDates(Math.max(1, Math.min(daysDiff, 365)));
        }

        if (Array.isArray(data) && data.length > 0) {
            return processHistoryDatesData(data);
        }
        setCalender([]);
        setCalendarItemBackground([]);
        return data;
    };

    const getMondayOfWeek = (d) => {
        const date = new Date(d);
        const day = date.getDay();
        const diff = date.getDate() - (day === 0 ? 7 : day) + 1;
        date.setDate(diff);
        date.setHours(0, 0, 0, 0);
        return date;
    };

    const processHistoryDatesData = (data) => {
            let rowsColumns = [];
            let bkgs = [];

            let currentMonth = -1;
            let currentMondayKey = null;
            let index = 0;

            let stopDate = new Date();
            const dataCopy = [...data];
            dataCopy.push(stopDate);
            
            // Normalize dates for proper comparison (date only, no time)
            let startDate = new Date(dataCopy[0]);
            startDate.setHours(0, 0, 0, 0);
            
            // Ensure we include today by normalizing to end of today
            let endDate = new Date(stopDate);
            endDate.setHours(23, 59, 59, 999);
            
            for (let dayDate = new Date(startDate); dayDate <= endDate; dayDate.setDate(dayDate.getDate() + 1)) {
                let dayInfo = global.getDateInfo(dayDate);
                const mondayOfWeek = getMondayOfWeek(dayDate);
                const mondayKey = mondayOfWeek.getTime();

                if (currentMonth !== dayInfo.month) {
                    currentMonth = dayInfo.month;
                    rowsColumns.push([[dayInfo.monthString + " " + dayInfo.year]]);
                }

                if (currentMondayKey !== mondayKey) {
                    currentMondayKey = mondayKey;
                    rowsColumns.push([[], [], [], [], [], [], []]);
                }
                else if (dayInfo.dayOfMonth === 1) {
                    rowsColumns.push([[], [], [], [], [], [], []]);
                }

                dayInfo.row = rowsColumns.length - 1;

                let times = dataCopy.filter(x => new Date(x).toDateString() === dayInfo.datePart);
                if (times && times.length > 0) {
                    for (let t = 0; t < times.length; t++) {
                        let time = times[t];
                        if (time === stopDate) {
                            continue;
                        }
                        let scanDayInfo = global.getJsonDateInfo(time);
                        scanDayInfo.index = index;
                        scanDayInfo.row = rowsColumns.length - 1;
                        // Use Monday-first column order for chronological left-to-right display
                        const colIndex = (dayInfo.dayOfWeek + 6) % 7;
                        rowsColumns[dayInfo.row][colIndex].push(scanDayInfo);

                        const selectedBg = getSelectedBackground();
                        if (value === "NONE") {
                            bkgs.push(selectedBg);
                        }
                        else if (value === "AM") {
                            if (scanDayInfo.time.indexOf("AM") >= 0) {
                                bkgs.push(selectedBg);
                            }
                            else {
                                bkgs.push("");
                            }
                        }
                        else if (value === "PM") {
                            if (scanDayInfo.time.indexOf("PM") >= 0) {
                                bkgs.push(selectedBg);
                            }
                            else {
                                bkgs.push("");
                            }
                        }
                        else {
                            bkgs.push(selectedBg);
                        }

                        index++;
                    }
                }
                else {
                    dayInfo.time = null;
                    const colIndex = (dayInfo.dayOfWeek + 6) % 7;
                    rowsColumns[dayInfo.row][colIndex].push(dayInfo);
                }
            }

            setCalendarItemBackground(bkgs);
            setCalender(rowsColumns);

            return data;
    };

    const fetchHistoryDates = async (days) => {
        var response = await createApiEndpoint().fetchUserSiteAllHistoryDatesWithDays("compressed", days);

        var data = {};
        if (!response) {
            navigate("/logout");
            return;
        }
        else if (response.data.fetchUserSiteAllHistoryDatesWithDays) {
            data = response.data.fetchUserSiteAllHistoryDatesWithDays;
        }
        else {
            data = response.data.getUserHistoryWithDateAndTime;
        }

        if (Array.isArray(data) && data.length > 0) {
            return processHistoryDatesData(data);
        }
        setCalender([]);
        setCalendarItemBackground([]);
        return data;
    };

    const groupCalendarByMonth = (rows) => {
        const months = [];
        let current = null;
        for (const row of rows || []) {
            const first = row?.[0]?.[0];
            const isHeader = Array.isArray(row) && row.length === 1 && typeof first === 'string';
            if (isHeader) {
                current = { title: first, weeks: [] };
                months.push(current);
            } else if (current) {
                current.weeks.push(row);
            } else {
                current = { title: '', weeks: [row] };
                months.push(current);
            }
        }
        return months;
    };

    const renderHistoryDatesList = (dates) => {
        const months = groupCalendarByMonth(calender);
        const borderColor = getThemeColor('#cccccc', 'rgba(244,204,142,0.5)');
        const textColor = getThemeColor('#333333', '#f4cc8e');

        return (
        <MuiMaterials.Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <MuiMaterials.RadioGroup
                key={`radio-group-${currentTheme}-${forceUpdate}`}
                value={value}
                defaultValue="ALL"
                name="radio-buttons-group"
                sx={{
                    color: textColor,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 0.5,
                    margin: '8px auto 16px',
                }}
            >
                <MuiMaterials.FormControlLabel
                    value="ALL"
                    control={<MuiMaterials.Radio
                        sx={{
                            color: textColor,
                            '&.Mui-checked': { color: textColor },
                        }}
                        onChange={e => handleRadioButtonChange(e, "ALL")}
                    />}
                    label="ALL"
                    sx={{ color: textColor }}
                />
                <MuiMaterials.FormControlLabel
                    value="AM"
                    control={<MuiMaterials.Radio
                        sx={{
                            color: textColor,
                            '&.Mui-checked': { color: textColor },
                        }}
                        onChange={e => handleRadioButtonChange(e, "AM")}
                    />}
                    label="AM"
                    sx={{ color: textColor }}
                />
                <MuiMaterials.FormControlLabel
                    value="PM"
                    control={<MuiMaterials.Radio
                        sx={{
                            color: textColor,
                            '&.Mui-checked': { color: textColor },
                        }}
                        onChange={(e) => handleRadioButtonChange(e, "PM")}
                    />}
                    label="PM"
                    sx={{ color: textColor }}
                />
                <MuiMaterials.FormControlLabel
                    value="NONE"
                    control={<MuiMaterials.Radio
                        sx={{
                            color: textColor,
                            '&.Mui-checked': { color: textColor },
                        }}
                        onChange={e => handleRadioButtonChange(e, "NONE")}
                    />}
                    label="NONE"
                    sx={{ color: textColor }}
                />
            </MuiMaterials.RadioGroup>

            <MuiMaterials.Box
                className="historyDateList"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    margin: '0 auto',
                }}
            >
                {months.map((month, midx) => (
                    <MuiMaterials.Box
                        key={`month-block-${midx}-${month.title}-${currentTheme}-${forceUpdate}`}
                        sx={{
                            flex: '0 0 280px',
                            width: 280,
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        {month.title ? (
                            <MuiMaterials.Box
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: textColor,
                                    textAlign: 'center',
                                    marginBottom: 0.75,
                                    letterSpacing: '0.02em',
                                }}
                            >
                                {month.title}
                            </MuiMaterials.Box>
                        ) : null}
                        <MuiMaterials.Box
                            sx={{
                                width: '100%',
                                border: `1px solid ${borderColor}`,
                                borderRadius: '4px',
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                            }}
                        >
                            {month.weeks.map((cal, cidx) => (
                                <MuiMaterials.Box
                                    key={`calendar-row-${midx}-${cidx}-${currentTheme}-${forceUpdate}`}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                                        width: '100%',
                                        color: textColor,
                                        borderTop: cidx === 0 ? 'none' : `1px solid ${borderColor}`,
                                    }}
                                >
                                    {(cal.length >= 7 ? cal : [...cal, ...Array(Math.max(0, 7 - cal.length)).fill([])]).slice(0, 7).map((d, didx) => (
                                        <MuiMaterials.Box
                                            key={`calendar-day-${midx}-${cidx}-${didx}-${currentTheme}-${forceUpdate}`}
                                            sx={{
                                                color: textColor,
                                                borderLeft: didx === 0 ? 'none' : `1px solid ${borderColor}`,
                                                padding: '3px 2px',
                                                minWidth: 0,
                                                minHeight: 52,
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'flex-start',
                                                gap: '1px',
                                                overflow: 'hidden',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {(Array.isArray(d) ? d : []).map((dayInfo, tidx) => {
                                                if (typeof dayInfo === 'string') {
                                                    return null;
                                                }

                                                let dayOfMonthElement = null;
                                                if (tidx === 0 && dayInfo?.dayOfMonth != null) {
                                                    dayOfMonthElement = (
                                                        <MuiMaterials.Box
                                                            key={`day-${midx}-${cidx}-${didx}-${tidx}-${currentTheme}-${forceUpdate}`}
                                                            sx={{
                                                                fontSize: '13px',
                                                                lineHeight: 1.15,
                                                                color: textColor,
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {dayInfo.dayOfMonth}
                                                        </MuiMaterials.Box>
                                                    );
                                                }

                                                let timeElement = null;
                                                if (dayInfo?.time) {
                                                    timeElement = (
                                                        <MuiMaterials.Box
                                                            key={`time-${dayInfo.index}-${currentTheme}-${forceUpdate}`}
                                                            className={"calendarTime"}
                                                            onClick={handleCalendarSelectionChange}
                                                            data-date={dayInfo.jsonDate}
                                                            data-calendar-index={dayInfo.index}
                                                            // Inline background so View/Email can still read selection via element.style
                                                            style={{
                                                                background: calendarItemBackground[dayInfo.index] ?? '',
                                                                color: textColor,
                                                            }}
                                                            sx={{
                                                                fontSize: '10px',
                                                                lineHeight: 1.2,
                                                                width: '100%',
                                                                cursor: 'pointer',
                                                                borderRadius: '2px',
                                                                px: 0.25,
                                                            }}
                                                        >
                                                            {dayInfo.time}
                                                        </MuiMaterials.Box>
                                                    );
                                                }

                                                return (
                                                    <Fragment key={`cell-part-${midx}-${cidx}-${didx}-${tidx}-${dayInfo?.index ?? tidx}`}>
                                                        {dayOfMonthElement}
                                                        {timeElement}
                                                    </Fragment>
                                                );
                                            })}
                                        </MuiMaterials.Box>
                                    ))}
                                </MuiMaterials.Box>
                            ))}
                        </MuiMaterials.Box>
                    </MuiMaterials.Box>
                ))}
            </MuiMaterials.Box>
        </MuiMaterials.Box>
        );
    };

    return (
        <>
            {renderHistoryDatesList(global.gw.selectedSite.temperatureComparison.historyDayDates)}
        </>
    );
}
export default ReportTemperatureHistoryDateList;